import axios from 'axios';
import { getApiBaseUrl, getApiRootUrl } from './api';

// -- types -------------------------------------------------------------------

export type StepType = 'ask' | 'send_template' | 'delay' | 'branch' | 'finish';
export type ResponseType = 'text' | 'number' | 'choice' | 'scale' | 'emoji';
export type TriggerType = 'schedule' | 'manual' | 'event';

export interface AskStep {
  id: string;
  type: 'ask';
  prompt: string;
  response_type: ResponseType;
  choices?: string[];
  /** Optional image attached to a specific choice. Map of {choice_label: url}.
   *  Renders in the simulator as a thumbnail on each quick-reply button. */
  choices_with_images?: Record<string, string>;
  /** Optional image attached to the prompt itself (e.g., "describe this picture"). */
  image_url?: string;
  image_caption?: string;
  scale_min?: number;
  scale_max?: number;
  store_as?: string;
  unit?: string;
  timeout_hours?: number;
  phi?: boolean;
  next?: string;
  description?: string;
  // Template fields — REQUIRED when this is the FIRST step of a scheduled
  // workflow (proactive send outside the 24h window). The Meta-approved
  // template's body must match `prompt` semantically; placeholders are
  // filled from `template_body_params` (e.g. "{{recipient.first_name}}").
  template_name?: string;
  template_language?: string;       // default 'en_US'
  template_body_params?: string[];
}

export interface SendTemplateStep {
  id: string;
  type: 'send_template';
  body?: string;                    // free-form (in-window only)
  template_name?: string;           // Meta-approved template (proactive-safe)
  template_language?: string;       // default 'en_US'
  template_body_params?: string[];
  variables?: Record<string, unknown>;
  next?: string;
  description?: string;
}

export interface DelayStep {
  id: string;
  type: 'delay';
  duration_minutes?: number;
  duration_hours?: number;
  duration_days?: number;
  next?: string;
  description?: string;
}

export interface BranchStep {
  id: string;
  type: 'branch';
  conditions?: Array<{ if: string; goto: string }>;
  default_goto?: string;
  description?: string;
}

export interface FinishStep {
  id: string;
  type: 'finish';
  on_complete?: Array<Record<string, unknown>>;
  description?: string;
}

export type Step = AskStep | SendTemplateStep | DelayStep | BranchStep | FinishStep;

export interface MissedResponsePolicy {
  reminder_after_hours?: number;
  reminder_message?: string;
  escalate_after_hours?: number;
  escalate_action?: Record<string, unknown>;
}

export interface WorkflowDefinition {
  steps: Step[];
  missed_response_policy?: MissedResponsePolicy;
  on_complete?: Array<Record<string, unknown>>;
}

export interface WorkflowTrigger {
  type: TriggerType;
  cron?: string;
  per_recipient_field?: string;
  timezone_field?: string;
  timezone?: string;
  audience?: { tags?: string[]; ids?: string[]; all?: boolean; all_tags?: string[] };
  event?: string;
  filter?: Record<string, unknown>;
}

export interface Workflow {
  id: string;
  client_id: string;
  name: string;
  description?: string | null;
  channel: 'whatsapp' | 'web' | 'any';
  trigger: WorkflowTrigger;
  definition: WorkflowDefinition;
  active: boolean;
  version: number;
  created_at: string;
  updated_at: string;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  use_cases?: string[];
  channel: string;
  trigger: WorkflowTrigger;
  definition: WorkflowDefinition;
}

export interface WorkflowSession {
  id: string;
  recipient_id: string | null;
  current_step_id: string | null;
  responses: Record<string, unknown>;
  status: 'active' | 'completed' | 'errored' | 'paused' | 'opted_out';
  next_action_at: string | null;
  started_at: string;
  last_activity_at: string;
  completed_at: string | null;
}

export interface CreateWorkflowInput {
  name: string;
  description?: string;
  channel?: 'whatsapp' | 'web' | 'any';
  trigger?: WorkflowTrigger;
  definition?: WorkflowDefinition;
  active?: boolean;
}

export interface UpdateWorkflowInput {
  name?: string;
  description?: string;
  channel?: 'whatsapp' | 'web' | 'any';
  trigger?: WorkflowTrigger;
  definition?: WorkflowDefinition;
  active?: boolean;
  bump_version?: boolean;
}

// -- API ---------------------------------------------------------------------

const apiBase = () => getApiBaseUrl();
const clientBase = (clientId: string) => `${apiBase()}/clients/${clientId}/workflows`;

export const workflowsApi = {
  async listTemplates(): Promise<WorkflowTemplate[]> {
    const res = await axios.get<{ templates: WorkflowTemplate[] }>(`${apiBase()}/workflow-templates`);
    return res.data.templates;
  },

  async list(clientId: string, opts: { active?: boolean } = {}): Promise<Workflow[]> {
    const res = await axios.get<{ workflows: Workflow[] }>(clientBase(clientId), { params: opts });
    return res.data.workflows;
  },

  async get(clientId: string, workflowId: string): Promise<Workflow> {
    const res = await axios.get<{ workflow: Workflow }>(`${clientBase(clientId)}/${workflowId}`);
    return res.data.workflow;
  },

  async create(clientId: string, input: CreateWorkflowInput): Promise<Workflow> {
    const res = await axios.post<{ workflow: Workflow }>(clientBase(clientId), input);
    return res.data.workflow;
  },

  async update(clientId: string, workflowId: string, input: UpdateWorkflowInput): Promise<Workflow> {
    const res = await axios.patch<{ workflow: Workflow }>(`${clientBase(clientId)}/${workflowId}`, input);
    return res.data.workflow;
  },

  async remove(clientId: string, workflowId: string): Promise<void> {
    await axios.delete(`${clientBase(clientId)}/${workflowId}`);
  },

  async runNow(
    clientId: string,
    workflowId: string,
    audience?: WorkflowTrigger['audience'],
  ): Promise<{ ok: boolean; enrolled: number }> {
    const res = await axios.post<{ ok: boolean; enrolled: number }>(
      `${clientBase(clientId)}/${workflowId}/run`,
      { audience },
    );
    return res.data;
  },

  async listSessions(
    clientId: string,
    workflowId: string,
    opts: { status?: string; limit?: number } = {},
  ): Promise<{ sessions: WorkflowSession[]; total: number }> {
    const res = await axios.get<{ sessions: WorkflowSession[]; total: number }>(
      `${clientBase(clientId)}/${workflowId}/sessions`,
      { params: opts },
    );
    return res.data;
  },

  async setRecipientSchedule(
    clientId: string,
    recipientId: string,
    input: { timezone?: string; preferred_time_local?: string },
  ): Promise<{ recipient_id: string; timezone: string | null; preferred_time_local: string | null }> {
    const res = await axios.patch(
      `${apiBase()}/clients/${clientId}/recipients/${recipientId}/schedule`,
      input,
    );
    return res.data;
  },

  /**
   * Upload an image for a workflow choice. Returned `url` is absolute and
   * publicly fetchable (so Meta can pull it at send time). Caller writes
   * the URL into `step.choices_with_images[choiceLabel]`.
   */
  async uploadAsset(
    clientId: string,
    file: File,
  ): Promise<{ url: string; filename: string; size: number; content_type: string }> {
    const form = new FormData();
    form.append('file', file);
    const res = await axios.post<{
      data: { url: string; filename: string; size: number; content_type: string };
    }>(`${apiBase()}/clients/${clientId}/workflow-assets`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  },

  /**
   * Rewrite a stored asset URL for in-editor PREVIEW only. The stored URL is
   * absolute against APP_URL (ngrok in dev / real domain in prod) so Meta can
   * fetch it at send time — but ngrok-free serves a browser warning page that
   * breaks <img> loading. For preview we strip to the `/uploads/...` path and
   * load it from the local API root origin instead. Pass-through for already-
   * relative or data URLs.
   */
  previewUrl(stored?: string): string {
    if (!stored) return '';
    if (stored.startsWith('data:') || stored.startsWith('blob:')) return stored;
    const idx = stored.indexOf('/uploads/');
    if (idx === -1) return stored;
    const path = stored.slice(idx);
    const root = (getApiRootUrl() || '').replace(/\/$/, '');
    return `${root}${path}`;
  },
};
